import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Stethoscope, CheckCircle2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { first_name: "", middle_name: "", last_name: "", address: "", birthday: "", contact_number: "", reason: "" };

export default function KioskRegister() {
  const { slug, doctorId } = useParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [chosenDoctor, setChosenDoctor] = useState(doctorId || "");
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/kiosk/${slug}`);
        setInfo(data);
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      }
    })();
  }, [slug]);

  const lockedDoctor = useMemo(
    () => (doctorId ? (info?.doctors || []).find((d) => d.id === doctorId) : null),
    [doctorId, info]
  );

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Please enter your first name and surname");
    setBusy(true);
    try {
      const { data } = await api.post("/public/kiosk/register", {
        hospital_id: slug,
        doctor_id: doctorId || chosenDoctor || "",
        first_name: form.first_name,
        middle_name: form.middle_name,
        last_name: form.last_name,
        address: form.address,
        birthday: form.birthday,
        contact_number: form.contact_number,
        reason: form.reason,
      });
      setTicket(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setForm(EMPTY); setChosenDoctor(doctorId || ""); setTicket(null); };

  if (!info) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  // ---- Confirmation / ticket ----
  if (ticket) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-3xl shadow-sm p-10 text-center">
          <CheckCircle2 size={64} className="mx-auto text-emerald-600" />
          <div className="text-slate-500 mt-4 text-lg">You are registered</div>
          <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mt-6">Your Queue Number</div>
          <div className="text-8xl font-black text-blue-700 mt-2" style={{ fontFamily: "Work Sans" }}>{ticket.queue_number}</div>
          <div className="mt-4 text-xl font-semibold text-slate-900">{ticket.patient_name}</div>
          {ticket.doctor_name ? <div className="text-slate-600 mt-1">for {ticket.doctor_name}</div> : null}
          <p className="mt-6 text-slate-500">Please take a seat and wait for your number to be called.</p>
          <Button onClick={reset} className="mt-8 h-14 w-full text-lg bg-blue-700 hover:bg-blue-800">
            <UserPlus size={20} className="mr-2" /> Register another patient
          </Button>
        </div>
      </div>
    );
  }

  // ---- Registration form ----
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-blue-800 to-cyan-500 text-white">
        <div className="max-w-2xl mx-auto px-6 py-8 text-center">
          <div className="text-xs tracking-[0.32em] text-white/80">WALK-IN REGISTRATION</div>
          <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ fontFamily: "Work Sans" }}>{info.hospital_name}</h1>
          {lockedDoctor ? (
            <div className="mt-2 inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1 text-sm">
              <Stethoscope size={15} /> {lockedDoctor.name}{lockedDoctor.specialty ? ` · ${lockedDoctor.specialty}` : ""}
            </div>
          ) : null}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <p className="text-slate-600">Fill out your details to join the queue.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">First name *</label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-14 text-lg mt-1" data-testid="kiosk-first-name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Middle name</label>
              <Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} placeholder="Optional" className="h-14 text-lg mt-1" data-testid="kiosk-middle-name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Surname *</label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-14 text-lg mt-1" data-testid="kiosk-last-name" />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Address</label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-14 text-lg mt-1" data-testid="kiosk-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">Birthday</label>
              <Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className="h-14 text-lg mt-1" data-testid="kiosk-birthday" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Contact number</label>
              <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="h-14 text-lg mt-1" data-testid="kiosk-contact" />
            </div>
          </div>

          {!doctorId && (info.doctors || []).length > 0 ? (
            <div>
              <label className="text-sm font-semibold text-slate-700">Doctor (optional)</label>
              <select value={chosenDoctor} onChange={(e) => setChosenDoctor(e.target.value)} className="h-14 text-lg mt-1 w-full border border-slate-300 rounded-lg px-3" data-testid="kiosk-doctor">
                <option value="">Any available doctor</option>
                {(info.doctors || []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ""}</option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <label className="text-sm font-semibold text-slate-700">Complaint / Concern</label>
            <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3}
              placeholder="Briefly describe why you're here today"
              className="mt-1 w-full border border-slate-300 rounded-lg p-3 text-lg" data-testid="kiosk-reason" />
          </div>

          <Button onClick={submit} disabled={busy} className="w-full h-16 text-xl bg-blue-700 hover:bg-blue-800" data-testid="kiosk-submit">
            {busy ? "Registering…" : "Get my number"}
          </Button>
        </div>
      </main>
    </div>
  );
}
